'use client';

import { useRouter, usePathname } from 'next/navigation';
import { useTransition, useState, useEffect } from 'react';
import { SELECTABLE_LANGUAGES } from '@/lib/translator/languages';

export function LanguageSwitcher() {
  const router    = useRouter();
  const pathname  = usePathname();
  const [isPending, startTransition] = useTransition();
  const [current, setCurrent] = useState('en');
  const [open, setOpen]       = useState(false);

  useEffect(() => {
    // Detect lang from URL segment (/en/..., /hu/..., etc.)
    const seg = pathname.split('/')[1];
    const match = SELECTABLE_LANGUAGES.find((l) => l.code === seg);
    if (match) setCurrent(match.code);
  }, [pathname]);

  const select = (code: string) => {
    setOpen(false);
    setCurrent(code);
    startTransition(() => {
      // Swap the first path segment (lang) with the new code
      const segments = pathname.split('/');
      segments[1] = code;
      router.push(segments.join('/'));
    });
  };

  const active = SELECTABLE_LANGUAGES.find((l) => l.code === current) ?? SELECTABLE_LANGUAGES[0];

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        disabled={isPending}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-sm font-medium text-zinc-300 transition-colors disabled:opacity-50"
      >
        <span>{active.flag}</span>
        <span>{active.label}</span>
        <svg className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="m19 9-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-20 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl overflow-hidden min-w-[100px]">
            {SELECTABLE_LANGUAGES.map((l) => (
              <button
                key={l.code}
                onClick={() => select(l.code)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-zinc-700 transition-colors ${
                  l.code === current ? 'text-brand-400 font-semibold' : 'text-zinc-300'
                }`}
              >
                <span>{l.flag}</span>
                <span>{l.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
